import React from 'react';
import { DateTimeInput } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import moment from 'moment';

const PLACEHOLDER_PATTERN = 'GG/AA/YYYY SS:dd';

const SECTIONS = [
  { len: 2, placeholder: 'GG', min: 1, max: 31 },
  { len: 2, placeholder: 'AA', min: 1, max: 12 },
  { len: 4, placeholder: 'YYYY' },
  { len: 2, placeholder: 'SS', min: 0, max: 23 },
  { len: 2, placeholder: 'dd', min: 0, max: 59 },
];

const clampSection = (value, min, max) => {
  if (value.length < 2) return value;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '';
  return String(Math.max(min, Math.min(max, numeric))).padStart(2, '0');
};

/** Doldurulan rakamlar + kalan placeholder (örn. 29/07/YYYY SS:dd) */
const formatDigitsWithPlaceholder = (digits) => {
  let offset = 0;
  const parts = SECTIONS.map((section) => {
    let raw = digits.slice(offset, offset + section.len);
    offset += section.len;
    if (section.max != null && raw.length === section.len) {
      raw = clampSection(raw, section.min, section.max);
    }
    return raw + section.placeholder.slice(raw.length);
  });
  return `${parts[0]}/${parts[1]}/${parts[2]} ${parts[3]}:${parts[4]}`;
};

/** Placeholder harflerini at; sadece gerçek rakamları al */
const extractDigits = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/[GAYSd]/g, '')
    .replace(/\D/g, '')
    .slice(0, 12);
};

const caretAfterDigits = (formatted, digitCount) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === digitCount) return i + 1;
    }
  }
  return formatted.length;
};

const toDisplayValue = (value) => {
  if (!value) return PLACEHOLDER_PATTERN;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : PLACEHOLDER_PATTERN;
};

const parseDisplayValue = (value) => {
  const digits = extractDigits(value);
  if (digits.length < 12) return null;
  const formatted = formatDigitsWithPlaceholder(digits);
  const parsed = moment(formatted, 'DD/MM/YYYY HH:mm', true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const MaskedDateTimeInput = ({
  source,
  label,
  disabled = false,
  helperText,
  validate,
}) => {
  const { setValue } = useFormContext();
  const fieldValue = useWatch({ name: source });
  const [displayValue, setDisplayValue] = React.useState(PLACEHOLDER_PATTERN);
  const [isFocused, setIsFocused] = React.useState(false);
  const inputRef = React.useRef(null);
  const pendingCaret = React.useRef(null);
  const lastCaretRef = React.useRef(0);

  React.useEffect(() => {
    // Kullanıcı yazarken form sync display'i ezmesin
    if (isFocused) return;
    const next = toDisplayValue(fieldValue);
    setDisplayValue((current) => (current === next ? current : next));
  }, [fieldValue, isFocused]);

  React.useLayoutEffect(() => {
    if (pendingCaret.current == null || !inputRef.current) return;
    const pos = pendingCaret.current;
    pendingCaret.current = null;
    inputRef.current.setSelectionRange(pos, pos);
  }, [displayValue]);

  const syncValue = React.useCallback(
    (nextDisplayValue) => {
      const digits = extractDigits(nextDisplayValue);
      if (!digits) {
        setValue(source, null, { shouldDirty: true, shouldValidate: true });
        return;
      }
      const parsed = parseDisplayValue(nextDisplayValue);
      if (parsed) {
        setValue(source, parsed, { shouldDirty: true, shouldValidate: true });
      }
    },
    [setValue, source],
  );

  const applyDigits = (digits, caretPos) => {
    const nextDisplayValue = formatDigitsWithPlaceholder(digits);
    pendingCaret.current = caretPos;
    setDisplayValue(nextDisplayValue);
    syncValue(nextDisplayValue);
  };

  const handleChange = (event) => {
    const rawValue = event.target.value;
    const prevDigits = extractDigits(displayValue);
    const nextDigits = extractDigits(rawValue);
    const endOfPrevDigits = caretAfterDigits(displayValue, prevDigits.length);
    // Sondan yazıyorsa caret ilerlesin; mouse ile ortadan tıklanmışsa zıplatma
    const typingAtEnd = lastCaretRef.current >= endOfPrevDigits;
    const caretPos = typingAtEnd
      ? caretAfterDigits(formatDigitsWithPlaceholder(nextDigits), nextDigits.length)
      : Math.min(lastCaretRef.current, formatDigitsWithPlaceholder(nextDigits).length);

    applyDigits(nextDigits, caretPos);
  };

  const handleSelect = (event) => {
    lastCaretRef.current = event.target.selectionStart ?? 0;
  };

  const handleFocus = (event) => {
    setIsFocused(true);
    if (extractDigits(displayValue).length === 0) {
      setDisplayValue(PLACEHOLDER_PATTERN);
      window.requestAnimationFrame(() => {
        event.target.setSelectionRange(0, 0);
        lastCaretRef.current = 0;
      });
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const digits = extractDigits(displayValue);
    if (!digits) {
      setDisplayValue(PLACEHOLDER_PATTERN);
      setValue(source, null, { shouldDirty: true, shouldValidate: true });
      return;
    }
    const parsed = parseDisplayValue(displayValue);
    if (parsed) {
      setDisplayValue(moment(parsed).format('DD/MM/YYYY HH:mm'));
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Backspace') return;
    const input = event.target;
    const digits = extractDigits(displayValue);
    if (!digits) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start == null || start !== end) return;

    const charBefore = displayValue[start - 1];
    // Separator veya placeholder üzerinde backspace → önceki rakamı sil
    if (charBefore && /[GAYSd/\s:]/.test(charBefore)) {
      event.preventDefault();
      const nextDigits = digits.slice(0, -1);
      applyDigits(
        nextDigits,
        caretAfterDigits(formatDigitsWithPlaceholder(nextDigits), nextDigits.length),
      );
    }
  };

  return (
    <React.Fragment>
      <DateTimeInput
        source={source}
        label={label}
        fullWidth
        disabled
        sx={{ display: 'none' }}
        validate={validate}
      />
      <TextField
        label={label}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        fullWidth
        disabled={disabled}
        margin="dense"
        size="small"
        inputRef={inputRef}
        InputLabelProps={{ shrink: true }}
        helperText={helperText}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'off',
          spellCheck: false,
        }}
        sx={{
          '& input': {
            letterSpacing: '0.02em',
            fontVariantNumeric: 'tabular-nums',
          },
        }}
      />
    </React.Fragment>
  );
};
